// Drawer custom element -- focus trap, ESC/outside-click close, ARIA state -- SG 20260703
declare global {
  interface Window {
    trapFocus?: (container: HTMLElement, elementToFocus?: HTMLElement) => void;
    removeTrapFocus?: (elementToFocus?: HTMLElement | null) => void;
  }
}

export class Drawer extends HTMLElement {
  private openButtons: NodeListOf<HTMLElement> | null = null;
  private hashOpeners: NodeListOf<HTMLAnchorElement> | null = null;
  private closeButton: HTMLButtonElement | null = null;
  private overlay: HTMLElement | null = null;
  private isOpen: boolean = false;
  private drawerId: string = "";
  private lastOpener: HTMLElement | null = null;
  private boundKeydown: ((event: KeyboardEvent) => void) | null = null;

  constructor() {
    super();
    this.drawerId = this.dataset.drawerId || "";
    this.closeButton = this.querySelector(".drawer__close-button");
    this.overlay = this.querySelector(".drawer__overlay");

    if (!this.hasAttribute("aria-hidden")) {
      this.setAttribute("aria-hidden", "true");
    }

    this.bindEvents();
  }

  bindEvents() {
    // Find all opener buttons for this drawer
    this.openButtons = document.querySelectorAll(`[data-drawer-opener="${this.drawerId}"]`);
    this.hashOpeners = document.querySelectorAll(`a[href="#${this.drawerId}"]`);

    // Bind open events and baseline ARIA on openers
    this.openButtons?.forEach((button) => {
      this.ensureOpenerAria(button);
      button.addEventListener("click", (event) => this.open(event));
    });

    this.hashOpeners?.forEach((link) => {
      this.ensureOpenerAria(link);
      link.addEventListener("click", (event) => {
        event.preventDefault();
        this.open(event);
      });
    });

    // Bind close events
    this.closeButton?.addEventListener("click", () => this.close());
    this.overlay?.addEventListener("click", () => this.close());

    // Close on ESC key when this drawer is open
    this.boundKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && this.isOpen) {
        event.preventDefault();
        this.close();
      }
    };
    document.addEventListener("keydown", this.boundKeydown);

    // Handle URL fragment for deep linking
    window.addEventListener("load", this.checkUrlHash.bind(this));
    window.addEventListener("hashchange", this.checkUrlHash.bind(this));
  }

  /**
   * Ensure opener controls expose dialog popup semantics
   */
  private ensureOpenerAria(opener: HTMLElement) {
    if (!opener.hasAttribute("aria-haspopup")) {
      opener.setAttribute("aria-haspopup", "dialog");
    }
    if (!opener.hasAttribute("aria-controls")) {
      opener.setAttribute("aria-controls", this.drawerId);
    }
    if (!opener.hasAttribute("aria-expanded")) {
      opener.setAttribute("aria-expanded", "false");
    }
  }

  /**
   * Toggle aria-expanded on all openers for this drawer
   */
  private setOpenersExpanded(expanded: boolean) {
    const value = expanded ? "true" : "false";
    document.querySelectorAll(`[data-drawer-opener="${this.drawerId}"]`).forEach((el) => {
      el.setAttribute("aria-expanded", value);
    });
    document.querySelectorAll(`a[href="#${this.drawerId}"]`).forEach((el) => {
      el.setAttribute("aria-expanded", value);
    });
  }

  /**
   * Check if URL hash matches this drawer's ID for deep linking
   */
  checkUrlHash() {
    const hash = window.location.hash.substring(1);
    if (hash === this.drawerId) {
      if (this.drawerId === "tog-guide") {
        history.replaceState("", document.title, window.location.pathname + window.location.search);
        return;
      }
      this.open();
    }
  }

  /**
   * Open the drawer
   */
  open(event?: Event) {
    const opener = event?.currentTarget as HTMLElement | undefined;
    if (opener) {
      this.lastOpener = opener;
    }

    // Close any other open drawers first
    document.querySelectorAll("drawer-component[open]").forEach((drawer) => {
      if (drawer !== this) {
        (drawer as Drawer).close();
      }
    });

    this.setAttribute("open", "");
    this.setAttribute("aria-hidden", "false");
    this.isOpen = true;
    document.body.style.overflow = "hidden";
    this.setOpenersExpanded(true);

    if (this.hasAttribute("data-trap-focus") && this.closeButton && typeof window.trapFocus === "function") {
      window.trapFocus(this, this.closeButton);
    } else if (this.closeButton) {
      this.closeButton.focus();
    }

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent("drawer:opened", {
        bubbles: true,
        detail: { drawerId: this.drawerId },
      })
    );
  }

  /**
   * Close the drawer
   */
  close() {
    const focusTarget = this.lastOpener;

    this.removeAttribute("open");
    this.setAttribute("aria-hidden", "true");
    this.isOpen = false;
    document.body.style.overflow = "";
    this.setOpenersExpanded(false);

    // Remove the hash from URL if it corresponds to this drawer
    if (window.location.hash.substring(1) === this.drawerId) {
      history.pushState("", document.title, window.location.pathname + window.location.search);
    }

    if (this.hasAttribute("data-trap-focus") && typeof window.removeTrapFocus === "function") {
      window.removeTrapFocus(focusTarget);
    } else if (focusTarget) {
      focusTarget.focus();
    }

    this.lastOpener = null;

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent("drawer:closed", {
        bubbles: true,
        detail: { drawerId: this.drawerId },
      })
    );
  }
}

customElements.define("drawer-component", Drawer);
